import type { ComponentPropsWithoutRef } from "react";

import { closestCorners, DndContext, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { twMerge } from "tailwind-merge";

import { ReorderHandle } from "src/base-components/ReorderHandle";

type SortableFieldsProps = {
  items: string[];
  children: React.ReactNode;
  onChangeOrder: (items: { oldIndex: number; newIndex: number }) => void;
  disabled?: boolean;
};

export const SortableFieldsContext: React.FC<SortableFieldsProps> = ({
  items,
  children,
  onChangeOrder,
  disabled,
}) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);

      onChangeOrder({
        oldIndex,
        newIndex,
      });
    }
  };

  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        disabled={disabled}
        items={items}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

type SortableFieldProps<T extends React.ElementType = "div"> = {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
  as?: T;
} & Omit<
  ComponentPropsWithoutRef<T>,
  "id" | "children" | "disabled" | "as" | "style"
>;

export const SortableField = <T extends React.ElementType = "div">({
  id,
  children,
  disabled,
  as,
  ...rest
}: SortableFieldProps<T>) => {
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      // A plain boolean only disables dragging, not being a drop target —
      // pass both explicitly so a disabled row can never receive a drop.
      disabled: disabled ? { draggable: true, droppable: true } : undefined,
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const Wrapper = (as ?? "div") as React.ElementType;
  const { className, ...restProps } = rest as typeof rest & {
    className?: string;
  };

  return (
    <Wrapper
      ref={setNodeRef}
      style={style}
      {...restProps}
      className={twMerge("relative", isDragging && "z-10 bg-white", className)}
    >
      <div className="flex items-start gap-x-2">
        {!disabled && (
          <div className="pt-1">
            <ReorderHandle
              color="text-gray-400"
              listeners={listeners}
              size="xs"
            />
          </div>
        )}
        <div className="min-w-0 max-w-full flex-1">{children}</div>
      </div>
    </Wrapper>
  );
};
